// src/pages/DashboardNew.js

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo
} from 'react';
import PlayerStats from '../components/PlayerStatsNew';
import AICoachOpenMic from '../components/AICoachOpenMic';
import QuestCard from '../components/QuestCard';
import VerificationModal from '../components/VerificationModal';
import MindSessionModal from '../components/MindSessionModal';
import QuestDetailsModal from '../components/QuestDetailsModal'; // Import the new modal
import CoachSessionModal from '../components/CoachSessionModal'; // Import the new modal
import RecoveryActivatedModal from '../components/RecoveryActivatedModal'; // Import Recovery Modal
import QuestGenerationLoading from '../components/QuestGenerationLoading'; // Import Loading Component
import ShoppingListDrawer from '../components/ShoppingListDrawer';
import BicepIcon from '../components/icons/BicepIcon';
import MeditateIcon from '../components/icons/MeditateIcon';
import CutleryIcon from '../components/icons/CutleryIcon';
import { Brain } from 'lucide-react';

import {
  getAiFeatureQuotas,
  getQuestGenerationStatus
} from '../api/dataService';
import {
  useAuth
} from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { FeatureBanner } from '../components/FeatureBanner';
import { useSSEEvent } from '../context/SSEContext'; // Import SSE hook
import { useDashboardData } from '../hooks/useDashboardData';
import { useDashboardRefresh } from '../context/DashboardRefreshContext';
import { startVadPreload } from '../utils/vadPreloader';
import { buildVisibleTodayQuests } from '../utils/dashboardQuestList';
import { getLocalDateKey } from '../utils/calendarMoveSwap';
import { buildHealthKitSlides } from '../utils/playerStatsSlides';
import { resolveWorkoutTargetKcal } from '../utils/fitnessStats';
import { VERIFICATION_MODAL_FEATURE_KEYS } from '../utils/aiFeatureQuotas';
import { resolveDisplayName } from '../utils/displayName';
import {
  getQuestGenerationLoadingCopy,
  shouldBootstrapQuestGenerationPoll,
} from '../utils/questGenerationUi';
import { useLocale } from '../context/LocaleContext';
import { detectBrowserTimezone } from '../utils/timezone';

const DashboardNew = () => {
  const { t } = useLocale();
  const [quests, setQuests] = useState([]);
  const [playerStats, setPlayerStats] = useState(null);
  const [verifyingQuest, setVerifyingQuest] = useState(null); // Holds the quest object for the verification modal
  const [activeMindQuest, setActiveMindQuest] = useState(null);
  const [viewingQuestDetails, setViewingQuestDetails] = useState(null); // Holds the quest object for the details modal
  const [coachingQuest, setCoachingQuest] = useState(null); // Holds the quest for the AI Coach session
  const [isGenerating, setIsGenerating] = useState(false); // NEW: State to track background generation
  const [generationStatus, setGenerationStatus] = useState({ state: 'idle', job: null });
  const [generationError, setGenerationError] = useState(null);
  const [generationRequestStatus, setGenerationRequestStatus] = useState(null);
  const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);
  const [generationFinished, setGenerationFinished] = useState(false); // NEW: Track completion animation
  const [verificationAiFeatureQuotas, setVerificationAiFeatureQuotas] = useState(null);
  const pollingRef = useRef(null);
  const bootstrapPollingRef = useRef(null);
  const hasInitialFetch = useRef(false);
  const navFetchSkipFirst = useRef(true);
  const [pendingProgressEvent, setPendingProgressEvent] = useState(null);
  const [pendingVerificationResult, setPendingVerificationResult] = useState(null);

  // ... inside component ...
  const {
    user,
    setUser
  } = useAuth();

  // SSE Event Listeners
  useSSEEvent('quest_generation_completed', (data) => {
    console.log('[DashboardNew] SSE Received: quest_generation_completed', data);
    setGenerationFinished(true); // Trigger completion animation
    setGenerationError(null);
    setGenerationRequestStatus(null);
    // Note: We don't call refreshData() here immediately. We wait for the animation to finish.
  });

  useSSEEvent('quest_generation_failed', (data) => {
    console.log('[DashboardNew] SSE Received: quest_generation_failed', data);
    setIsGenerating(false);
    setGenerationFinished(false);
    setGenerationError(data.error || t('dashboard.unknownError', 'Unknown error'));
    setGenerationRequestStatus(null);
  });

  // Listen for quest completion from AI Coach to refresh UI
  useSSEEvent('quest_completed', (data) => {
    console.log('[DashboardNew] SSE Received: quest_completed', data);
    if (data?.questId && coachingQuest?._id === data.questId) {
      setCoachingQuest(null);
    }
    refreshData();
  });

  useSSEEvent('quest_swapped', (data) => {
    console.log('[DashboardNew] SSE Received: quest_swapped', data);
    refreshData();
  });

  // ... existing code ...

  // REMOVED: Polling Logic (useEffect with setInterval)
  const location = useLocation(); // <-- 2. GET THE CURRENT LOCATION OBJECT
  const navigate = useNavigate();
  const browserTimezone = useMemo(() => detectBrowserTimezone() || 'UTC', []);
  const questIcons = useMemo(() => ({
    'Workout': BicepIcon,
    'Mind': Brain,
    'Mindfulness': MeditateIcon,
    'Meal': CutleryIcon,
    'default': BicepIcon
  }), []);
  const resolveQuestIcon = useCallback((quest) => {
    return questIcons[quest?.type] || questIcons.default;
  }, [questIcons]);
  const { aggregate, generationStatus: remoteGenerationStatus, refreshData } = useDashboardData();
  const { refreshKey } = useDashboardRefresh();
  const formatTriggerSource = useCallback((source) => {
    if (!source || typeof source !== 'string') {
      return 'system';
    }
    return source
      .split(/[-_]/)
      .filter(Boolean)
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  }, []);

  useEffect(() => {
    if (remoteGenerationStatus) {
      setGenerationStatus(remoteGenerationStatus);
      if (!generationFinished) {
        setIsGenerating(remoteGenerationStatus.state === 'running');
      }
      if (remoteGenerationStatus.state === 'failed') {
        const errorMessage = remoteGenerationStatus.job?.errorMessage || t('dashboard.generationFailed', 'Quest generation failed.');
        setGenerationError(errorMessage);
        setGenerationRequestStatus(null);
      } else {
        setGenerationError(null);
        if (remoteGenerationStatus.state !== 'running' && !generationFinished) {
          setGenerationRequestStatus(null);
        }
      }
    }
  }, [remoteGenerationStatus, generationFinished]);

  useEffect(() => {
    const profile = aggregate?.user;
    const allQuests = aggregate?.quests;
    const teaserQuests = aggregate?.healthkitTeaserQuests || [];
    if (!profile) {
      return;
    }

    setUser(profile);

    setPlayerStats({
      level: profile.level,
      xp: profile.xp,
      xpToNextLevel: profile.xpToNextLevel,
      playerName: resolveDisplayName(profile),
      streak: profile.userStreak || 0,
      streakLastIncrementAt: profile.streakLastIncrementAt || null,
      aurasBalance: profile.aurasBalance || 0,
      aurasEffects: profile.aurasEffects || []
    });

    const todayDateKey = getLocalDateKey(new Date(), browserTimezone);

    setQuests(buildVisibleTodayQuests({
      quests: allQuests || [],
      teaserQuests,
      todayDateKey,
      now: new Date(),
      fallbackTimezone: browserTimezone,
      resolveQuestIcon
    }));
  }, [aggregate, browserTimezone, resolveQuestIcon, setUser]);

  useEffect(() => {
    let cancelled = false;
    getAiFeatureQuotas({ features: VERIFICATION_MODAL_FEATURE_KEYS })
      .then((response) => {
        if (!cancelled) {
          setVerificationAiFeatureQuotas(response.data || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('[DashboardNew] Failed to warm verification AI feature quotas', err);
        }
      });

    const handleQuotaRefresh = () => {
      getAiFeatureQuotas({ features: VERIFICATION_MODAL_FEATURE_KEYS, force: true })
        .then((response) => {
          if (!cancelled) {
            setVerificationAiFeatureQuotas(response.data || null);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.warn('[DashboardNew] Failed to refresh verification AI feature quotas', err);
          }
        });
    };

    window.addEventListener('forza:aiFeatureQuotasRefresh', handleQuotaRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener('forza:aiFeatureQuotasRefresh', handleQuotaRefresh);
    };
  }, []);

  // Nutrition Stats Calculation
  const nutritionStats = useMemo(() => {
    if (!aggregate) return null;

    const today = new Date();
    const startOfTodayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfTodayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    const allMealQuests = (aggregate.quests || []).filter(q => {
      if (q.type !== 'Meal' || !q.detailedDescription) return false;
      if (!q.start) return false;
      const questStartUTC = new Date(q.start).getTime();
      return questStartUTC >= startOfTodayUTC && questStartUTC <= endOfTodayUTC;
    });
    const log = aggregate.nutritionLog;

    const parseVal = (v) => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string') return parseFloat(v.replace(/[^0-9.]/g, '')) || 0;
      return 0;
    };

    let expected = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    let eaten = { calories: 0, protein: 0, carbs: 0, fat: 0 };

    // 1. Calculate Expected (Sum of today's meals)
    allMealQuests.forEach(q => {
      const desc = q.detailedDescription;
      expected.calories += parseVal(desc.totalCalories);
      if (desc.macronutrients) {
        expected.protein += parseVal(desc.macronutrients.protein);
        expected.carbs += parseVal(desc.macronutrients.carbs);
        expected.fat += parseVal(desc.macronutrients.fat);
      }
    });

    // 2. Calculate Eaten
    // A. Bulk Log Estimates
    if (log && log.estimatedTotals) {
      eaten.calories += parseVal(log.estimatedTotals.calories);
      eaten.protein += parseVal(log.estimatedTotals.protein);
      eaten.carbs += parseVal(log.estimatedTotals.carbs);
      eaten.fat += parseVal(log.estimatedTotals.fat);
    }

    // B. Individually Completed Meals (NOT included in bulk log)
    const bulkIncludedIds = new Set((log?.questIdsIncluded || []).map(id => id.toString()));

    allMealQuests.forEach(q => {
      // If completed AND NOT in the bulk log, we count its expected macros as eaten
      if (q.completed && !bulkIncludedIds.has(q._id.toString())) {
        const desc = q.detailedDescription;
        eaten.calories += parseVal(desc.totalCalories);
        if (desc.macronutrients) {
          eaten.protein += parseVal(desc.macronutrients.protein);
          eaten.carbs += parseVal(desc.macronutrients.carbs);
          eaten.fat += parseVal(desc.macronutrients.fat);
        }
      }
    });

    return { expected, eaten };
  }, [aggregate]);

  const fitnessStats = useMemo(() => {
    if (!aggregate) {
      return null;
    }

    const snapshot = aggregate.fitnessSnapshot || null;
    const timezone = aggregate.user?.userTimezone || browserTimezone;
    const activeEnergyKcal =
      typeof snapshot?.activeEnergyKcal === 'number' && Number.isFinite(snapshot.activeEnergyKcal)
        ? Math.round(snapshot.activeEnergyKcal)
        : null;
    const lastSyncedAt = snapshot?.lastSyncedAt || null;

    const workoutTargetKcal = resolveWorkoutTargetKcal({
      quests: aggregate.quests || [],
      snapshotDate: snapshot?.date || null,
      fallbackTimezone: timezone,
    });

    return {
      status: snapshot ? 'available' : 'unavailable',
      activeEnergyKcal,
      workoutTargetKcal,
      lastSyncedAt,
    };
  }, [aggregate, browserTimezone]);

  const healthkitSlides = useMemo(() => {
    if (!aggregate) {
      return [];
    }

    const timezone = aggregate.user?.userTimezone || browserTimezone;
    const todayDateKey = getLocalDateKey(new Date(), timezone);

    return buildHealthKitSlides({
      quests: aggregate.quests || [],
      teaserQuests: aggregate.healthkitTeaserQuests || [],
      snapshot: aggregate.fitnessSnapshot || null,
      todayDateKey,
      now: new Date(),
      fallbackTimezone: timezone,
      t,
    });
  }, [aggregate, browserTimezone, t]);

  useEffect(() => {
    if (hasInitialFetch.current) {
      refreshData();
    }
  }, [refreshKey, refreshData]);

  // --- 3. ADD THIS NEW useEffect TO DETECT THE INCOMING STATE ---
  useEffect(() => {
    // Check if we were navigated here with the 'isGenerating' state
    if (location.state?.isGenerating) {
      console.log('[DashboardNew] Received generation signal from previous page.');
      setIsGenerating(true);
      setGenerationError(null);
      setGenerationRequestStatus(location.state?.generationRequestStatus || 'started');
      // Clean the state from the location history so this doesn't re-trigger
      // on a page refresh or navigating back to the dashboard.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // NEW: Effect to refresh data when navigating back to dashboard
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath === '/dashboard') {
      if (navFetchSkipFirst.current) {
        navFetchSkipFirst.current = false;
        return undefined;
      }
      // Add a small delay to ensure navigation is complete
      const timer = setTimeout(() => {
        console.log('[DashboardNew] Navigated to dashboard, refreshing data...');
        refreshData();
      }, 300); // Small delay to ensure navigation is complete

      return () => clearTimeout(timer);
    }
  }, [location.pathname, refreshData]);

  // Polling logic removed in favor of SSE

  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      refreshData(); // Initial data fetch
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (bootstrapPollingRef.current) {
        clearInterval(bootstrapPollingRef.current);
        bootstrapPollingRef.current = null;
      }
    }
  }, [refreshData]);

  // Preload VAD model in the background for faster AI Coach startup
  useEffect(() => {
    const timer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(() => startVadPreload());
      } else {
        startVadPreload();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  // Fallback polling while generation is running to avoid stuck spinner if SSE is missed
  useEffect(() => {
    if (!isGenerating || generationFinished) { // Stop polling if finished
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const statusRes = await getQuestGenerationStatus();
        const statusData = statusRes?.data;
        if (!statusData) return;

        setGenerationStatus(statusData);
        if (statusData.state !== 'running') {
          if (statusData.state === 'failed') {
            setIsGenerating(false);
            setGenerationError(statusData.job?.errorMessage || t('dashboard.generationFailed', 'Quest generation failed.'));
          } else {
            setGenerationError(null);
            setGenerationFinished(true); // Trigger completion animation instead of immediate hide
          }
        }
      } catch (err) {
        console.error('[DashboardNew] Polling quest generation status failed', err);
      }
    };

    poll(); // initial immediate poll
    pollingRef.current = setInterval(poll, 4000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [isGenerating, generationFinished]); // Updated dependencies

  const shouldBootstrapStatusPoll = shouldBootstrapQuestGenerationPoll({
    aggregate,
    quests,
    generationStatus,
    isGenerating,
    generationFinished,
  });

  useEffect(() => {
    if (!shouldBootstrapStatusPoll) {
      if (bootstrapPollingRef.current) {
        clearInterval(bootstrapPollingRef.current);
        bootstrapPollingRef.current = null;
      }
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const stopBootstrapPolling = () => {
      if (bootstrapPollingRef.current) {
        clearInterval(bootstrapPollingRef.current);
        bootstrapPollingRef.current = null;
      }
    };

    const poll = async () => {
      attempts += 1;
      try {
        const statusRes = await getQuestGenerationStatus();
        if (cancelled) {
          return;
        }

        const statusData = statusRes?.data;
        if (!statusData) {
          return;
        }

        setGenerationStatus(statusData);

        if (statusData.state === 'running') {
          setGenerationError(null);
          setGenerationFinished(false);
          setGenerationRequestStatus('started');
          setIsGenerating(true);
          stopBootstrapPolling();
          return;
        }

        if (statusData.state === 'failed') {
          setGenerationError(statusData.job?.errorMessage || t('dashboard.generationFailed', 'Quest generation failed.'));
          setGenerationRequestStatus(null);
          stopBootstrapPolling();
          return;
        }

        if (statusData.job?.finishedAt) {
          stopBootstrapPolling();
          refreshData();
          return;
        }

        if (attempts >= 6) {
          stopBootstrapPolling();
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[DashboardNew] Bootstrap quest generation status poll failed', err);
        }
      }
    };

    poll();
    bootstrapPollingRef.current = setInterval(poll, 2500);

    return () => {
      cancelled = true;
      stopBootstrapPolling();
    };
  }, [refreshData, shouldBootstrapStatusPoll, t]);

  // Ensure the registration is always up-to-date (extra safety)
  const activeGenerationJob = generationStatus.state === 'running' ? generationStatus.job : null;
  const runningTriggerSource = activeGenerationJob ? formatTriggerSource(activeGenerationJob.triggerSource) : null;
  const runningStartedAt = activeGenerationJob?.startedAt ? new Date(activeGenerationJob.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
  const failedGenerationJob = generationStatus.state === 'failed' ? generationStatus.job : null;
  const failureFinishedAt = failedGenerationJob?.finishedAt ? new Date(failedGenerationJob.finishedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
  const loadingCopy = getQuestGenerationLoadingCopy(generationRequestStatus, t);

  const handleVerifyClick = (quest) => {
    if (quest?.type === 'Mind') {
      setActiveMindQuest(quest);
      return;
    }
    setVerifyingQuest(quest);
  };

  const handleViewDetails = (quest) => {
    setViewingQuestDetails(quest);
  };

  const handleCoachClick = (quest) => {
    setCoachingQuest(quest);
  };

  const handleCloseModal = () => {
    setVerifyingQuest(null);
  };

  const handleCloseMindModal = () => {
    setActiveMindQuest(null);
  };

  const handleCloseDetailsModal = () => {
    setViewingQuestDetails(null);
  };

  const handleCloseCoachModal = useCallback(() => {
    setCoachingQuest(null);
  }, []);

  const applyQuestCompletionSuccess = useCallback((payload, completedQuestId, closeModal) => {
    console.log('Verification successful. Preparing progress animation...');
    closeModal?.();

    if (payload && playerStats) {
      const xpDelta = payload.xpGained ?? 0;
      const completedQuestIds = Array.isArray(payload.questsCompleted) && payload.questsCompleted.length > 0
        ? payload.questsCompleted
        : (payload.questId || completedQuestId ? [payload.questId || completedQuestId] : []);
      const nextStats = {
        level: payload.newLevel ?? playerStats.level,
        xp: payload.newXp ?? (playerStats.xp + xpDelta),
        xpToNextLevel: payload.newXpToNextLevel ?? playerStats.xpToNextLevel,
        playerName: playerStats.playerName,
        streak: payload.streak ?? playerStats.streak,
        streakLastIncrementAt: payload.streakLastIncrementAt ?? playerStats.streakLastIncrementAt,
        aurasBalance: payload.aurasBalance ?? playerStats.aurasBalance ?? user?.aurasBalance ?? 0,
        aurasEffects: payload.aurasEffects ?? playerStats.aurasEffects ?? user?.aurasEffects ?? [],
      };

      setPendingVerificationResult({
        ...payload,
        questId: payload.questId || completedQuestId,
        questsCompleted: completedQuestIds,
      });
      setPendingProgressEvent({
        from: {
          xp: playerStats.xp,
          xpToNextLevel: playerStats.xpToNextLevel,
          level: playerStats.level,
        },
        to: {
          xp: nextStats.xp,
          xpToNextLevel: nextStats.xpToNextLevel,
          level: nextStats.level,
        },
        xpGained: xpDelta,
        leveledUp: Boolean(payload.leveledUp),
      });

      setPlayerStats(nextStats);
      setUser((prev) => {
        const base = prev && typeof prev === 'object' ? prev : {};
        return {
          ...base,
          level: nextStats.level,
          xp: nextStats.xp,
          xpToNextLevel: nextStats.xpToNextLevel,
          userStreak: nextStats.streak,
          streakLastIncrementAt: nextStats.streakLastIncrementAt,
          aurasBalance: nextStats.aurasBalance,
          aurasEffects: nextStats.aurasEffects,
        };
      });

      if (completedQuestIds.length > 0) {
        const completedQuestIdSet = new Set(completedQuestIds.map((id) => id?.toString()).filter(Boolean));
        setQuests((prev) =>
          prev.map((q) => (completedQuestIdSet.has(q._id?.toString()) ? { ...q, completed: true } : q))
        );
      }

      refreshData();
      return;
    }

    if (payload?.leveledUp) {
      setIsGenerating(true);
      setGenerationError(null);
    }
    refreshData();
  }, [playerStats, refreshData, setUser, user?.aurasBalance, user?.aurasEffects]);

  // This function is passed to the modal and called only on a successful verification.
  const handleVerificationSuccess = useCallback((payload) => {
    applyQuestCompletionSuccess(payload, verifyingQuest?._id, handleCloseModal);
  }, [applyQuestCompletionSuccess, verifyingQuest?._id]);

  const handleMindSessionSuccess = useCallback((payload) => {
    applyQuestCompletionSuccess(payload, activeMindQuest?._id, handleCloseMindModal);
  }, [activeMindQuest?._id, applyQuestCompletionSuccess]);

  const handleSessionComplete = useCallback((payload) => {
    console.log('Coach session complete. Refreshing data...');
    handleCloseCoachModal();
    if (payload && playerStats) {
      setPendingVerificationResult(payload);
      setPendingProgressEvent({
        from: {
          xp: playerStats.xp,
          xpToNextLevel: playerStats.xpToNextLevel,
          level: playerStats.level,
        },
        to: {
          xp: payload.newXp ?? playerStats.xp,
          xpToNextLevel: payload.newXpToNextLevel ?? playerStats.xpToNextLevel,
          level: payload.newLevel ?? playerStats.level,
        },
        xpGained: payload.xpGained ?? 0,
        leveledUp: Boolean(payload.leveledUp),
      });
      return;
    }
    refreshData();
  }, [refreshData, handleCloseCoachModal, playerStats]);

  const handleProgressAnimationComplete = useCallback(() => {
    if (!pendingVerificationResult || !playerStats) return;

    const payload = pendingVerificationResult;
    const nextStats = {
      level: payload.newLevel ?? playerStats.level,
      xp: payload.newXp ?? playerStats.xp,
      xpToNextLevel: payload.newXpToNextLevel ?? playerStats.xpToNextLevel,
      playerName: playerStats.playerName,
      streak: payload.streak ?? playerStats.streak,
      streakLastIncrementAt: payload.streakLastIncrementAt ?? playerStats.streakLastIncrementAt,
      aurasBalance: payload.aurasBalance ?? playerStats.aurasBalance ?? user?.aurasBalance ?? 0,
      aurasEffects: payload.aurasEffects ?? playerStats.aurasEffects ?? user?.aurasEffects ?? [],
    };

    setUser((prev) => {
      const base = prev && typeof prev === 'object' ? prev : {};
      return {
        ...base,
        level: nextStats.level,
        xp: nextStats.xp,
        xpToNextLevel: nextStats.xpToNextLevel,
        userStreak: nextStats.streak,
        streakLastIncrementAt: nextStats.streakLastIncrementAt,
        aurasBalance: nextStats.aurasBalance,
        aurasEffects: nextStats.aurasEffects,
      };
    });
    setPlayerStats(nextStats);
    setPendingProgressEvent(null);
    setPendingVerificationResult(null);

    if (payload.leveledUp) {
      setIsGenerating(true);
      setGenerationError(null);
    }
    refreshData();
  }, [refreshData, pendingVerificationResult, playerStats, setUser, user?.aurasBalance, user?.aurasEffects]);

  const isRecoveryMode = user?.isRecoveryMode;
  const recoveryDaysCompleted = user?.recoveryDaysCompleted || 0;
  // Voice Coach is always rendered; free-tier gating is handled inside AICoachOpenMic
  // using rewarded ads + negative credit.


  return (
    <div className="min-h-screen text-gray-300 font-sans">
      <div className="container mx-auto px-4 max-w-md">
        <main>
          <div>
            {playerStats ? (
              <PlayerStats
                {...playerStats}
                progressEvent={pendingProgressEvent}
                onProgressAnimationComplete={handleProgressAnimationComplete}
                nutritionStats={nutritionStats}
                fitnessStats={fitnessStats}
                healthkitSlides={healthkitSlides}
              />
            ) : (
              // Skeleton for PlayerStats
              <div className="bg-[#2C2F33] rounded-xl p-6 shadow-lg animate-pulse mb-6">
                <div className="h-4 bg-gray-600 rounded w-1/4 mb-4"></div>
                <div className="h-3 bg-gray-600 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-600 rounded w-3/4"></div>
              </div>
            )}
            <FeatureBanner />
            <AICoachOpenMic />

            {/* --- RECOVERY MODE BANNER --- */}
            {isRecoveryMode && (
              <div className="mt-6 bg-teal-900/40 border border-teal-500/30 rounded-xl p-4 flex items-center justify-between shadow-lg shadow-teal-900/20">
                <div className="flex items-center space-x-3">
                  <div className="bg-teal-500/20 p-2 rounded-full">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-teal-100 font-bold text-sm">{t('dashboard.recoveryModeActive', 'Recovery Mode Active')}</h3>
                    <p className="text-teal-400/80 text-xs">{t('dashboard.recoveryModeDescription', 'Complete 3 recovery workouts to restore full Level.')}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-white">{recoveryDaysCompleted}</span>
                  <span className="text-teal-400/60 text-sm font-medium">/3</span>
                </div>
              </div>
            )}

            <section className="mt-4">
              <h2 className="text-xl font-bold text-white mb-4">{t('dashboard.dailyQuests', 'Daily Quests')}</h2>
              {/* NEW: Conditional rendering for the generation state */}
              {generationError ? (
                <QuestGenerationLoading
                  isGenerating={false}
                  error={generationError}
                  triggerSource={runningTriggerSource}
                  startedAt={failureFinishedAt}
                />
              ) : isGenerating ? (
                <QuestGenerationLoading
                  isGenerating={true}
                  isFinished={generationFinished}
                  triggerSource={runningTriggerSource}
                  startedAt={runningStartedAt}
                  title={loadingCopy.title}
                  subtitle={loadingCopy.subtitle}
                  onComplete={() => {
                    setIsGenerating(false);
                    setGenerationFinished(false);
                    setGenerationRequestStatus(null);
                    refreshData();
                  }}
                />
              ) : (
                <div className="flex overflow-x-auto snap-x snap-mandatory space-x-4 pb-4 scrollbar-hide -mx-4 px-4">
                  {quests.length > 0 ? quests.map((quest) => (
                    <div key={quest._id} className="snap-center shrink-0 w-[85%] sm:w-[350px]">
                      <QuestCard
                        quest={quest}
                        handleVerifyClick={handleVerifyClick}
                        onViewDetails={handleViewDetails}
                        handleCoachClick={handleCoachClick}
                        activeAurasEffects={playerStats?.aurasEffects || []}
                        layout="vertical"
                      />
                    </div>
                  )) : (
                    // Skeleton for QuestCards
                    [...Array(3)].map((_, i) => (
                      <div key={i} className="snap-center shrink-0 w-[85%] sm:w-[350px] bg-[#2C2F33] rounded-xl p-4 shadow-lg animate-pulse">
                        <div className="flex items-center space-x-4">
                          <div className="bg-gray-600 rounded-lg w-12 h-12"></div>
                          <div className="flex-grow space-y-2">
                            <div className="h-4 bg-gray-600 rounded w-3/4"></div>
                            <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Weekly Shopping List CTA */}
              {!isGenerating && !generationError && quests.length > 0 && (
                <div className="mt-8 mb-4 w-full flex justify-center px-4">
                  <button
                    onClick={() => setIsShoppingListOpen(true)}
                    className="
                      w-full sm:w-[350px] relative overflow-hidden group/btn flex items-center justify-center
                      bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold py-3 rounded-xl text-sm
                      shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0
                      hover:from-cyan-400 hover:to-blue-500 hover:shadow-cyan-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400
                    "
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {t('dashboard.viewWeeklyShoppingList', 'View Weekly Shopping List')}
                    </span>
                  </button>
                </div>
              )}
            </section>
          </div>
        </main>
      </div >

      {/* Render Verification Modal */}
      {
        verifyingQuest && (
          <VerificationModal
            quest={verifyingQuest}
            allQuests={quests}
            initialAiFeatureQuotas={verificationAiFeatureQuotas}
            onClose={handleCloseModal}
            onVerificationSuccess={handleVerificationSuccess}
          />
        )
      }

      {
        activeMindQuest && (
          <MindSessionModal
            isOpen={!!activeMindQuest}
            quest={activeMindQuest}
            onClose={handleCloseMindModal}
            onComplete={handleMindSessionSuccess}
          />
        )
      }

      {/* Render Quest Details Modal */}
      {
        viewingQuestDetails && (
          <QuestDetailsModal
            quest={viewingQuestDetails}
            isOpen={!!viewingQuestDetails}
            onClose={handleCloseDetailsModal}
          />
        )
      }

      {/* Render Coach Session Modal */}
      {
        coachingQuest && (
          <CoachSessionModal
            quest={coachingQuest}
            onClose={handleCloseCoachModal}
            onSessionComplete={handleSessionComplete}
          />
        )
      }


      {/* Render Recovery Activated Modal */}
      {
        user && (
          <RecoveryActivatedModal
            user={user}
            onClose={() => { /* Optional: Handle close if needed, but modal handles its own logic */ }}
          />
        )
      }

      <ShoppingListDrawer
        isOpen={isShoppingListOpen}
        onClose={() => setIsShoppingListOpen(false)}
        userCheckedItems={user?.shoppingListCheckedItems || []}
      />
    </div >
  );
};

export default DashboardNew;
